<template>
    <div class="statistics" v-b-tooltip.hover.html="hoverMessage">
        <div class="info">
        <div class="icon" :class="['icon', this.alert ? 'icon-danger':'icon-success']">
            <i :class="this.alert ?'fas fa-exclamation':'fas fa-check'"/>
        </div>
        <h3 class="info-title">
            <animated-number :value="totalCount"/>
            <small v-if="secondaryCounts">&nbsp;(<animated-number :value="totalSecondaryCount"/>)</small>
        </h3>
        <h6 class="stats-title" style="height:2.2em; overflow-y:hidden;">
            <router-link :to="getLinkRoute()" v-if="routeName">
                <b>{{name}}</b>
            </router-link>
            <span v-else>{{name}}</span>
        </h6>
        <h6 v-show="totalValue != null" class="">{{totalValue | currency}}</h6>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import AnimatedNumber from "@/components/AnimatedNumber.vue";
    import { DashboardStatus } from '@/models/DashboardStatus';
    import { Voicemail, VoicemailGroupCount } from '@/models/Voicemail';
    @Component({
        name: "CounterIndicator",
        components: {
            AnimatedNumber
        }
    })
    export default class CounterIndicator extends Vue {
        @Prop() private name!: string;
        @Prop() private count:number|undefined;
        @Prop() private counts!: Array<DashboardStatus.CountByLocation>;
        @Prop() private countsVM!: Array<Voicemail>;
        @Prop() private routeName!: string;
        @Prop() private routeQuery?: object;
        @Prop({default: false}) private hideHoverMessage!: boolean;
        @Prop({default: null}) private secondaryCounts!: Array<DashboardStatus.CountByLocation> | null;
        @Prop({default: null}) private VMGroupCount!: Array<VoicemailGroupCount> | null;
        /**
         * If warningLimit not specified, alert will never be triggered.
         */
        @Prop({default: null}) private warningLimit!: number | null;
        @Prop({default: true}) private alertAboveLimit!: boolean;
        @Prop({default: ''}) private extraTooltip!: string;

        get alert():boolean{
            if(this.warningLimit == null) return false;

            if(this.alertAboveLimit) return this.totalCount >= this.warningLimit;
            else return this.totalCount <= this.warningLimit;
        }

        getLinkRoute() {
            let link: any = {
                name: this.routeName,
            };
            if (this.routeQuery) link.query = this.routeQuery
            return link;
        }

        get totalValue():number | null{
            if((this.counts?.filter(c => c.value != null).length ?? 0) == 0){
                return null;
            }
            return this.counts.reduce((total, c) => { return total + (c.value ?? 0)}, 0.0);
        }

        get totalCount():number{
            if (this.count != undefined) return this.count;
            if(this.name == 'Voicemail Queue'){
                return this.countsVM.length;
            }
            if(!this.counts?.length) return 0;
            let res = this.counts.map(c => c.count).reduce((a, b) => a+b, 0)
            return res;
        }

        get totalSecondaryCount():number{
            if(!this.secondaryCounts?.length) return 0;
            let res = this.secondaryCounts.map(c => c.count).reduce((a, b) => a+b, 0)
            return res;
        }

        get hoverMessage():string{
            if(this.name == 'Voicemail Queue'){
                 let resVM = '';
                if(this.VMGroupCount != null){
                      Object.entries(this.VMGroupCount).forEach(
                ([key,value])=>
                resVM +=  value.teamName + ': ' + value.voicemailCount + ', '
                )
                return resVM.slice(0,-2);
                }
                return '';
            }
            if(!this.counts?.length || this.hideHoverMessage) return '';

            let res = '';
            if(this.counts.map(c => c.count).reduce((a, b) => a+b, 0)) {
                const breakUpByStore = !this.counts.some(c => c.count > 1 && c.otherClassification);

                if(breakUpByStore) {
                    res = this.counts.filter(c => c.storeName).map(c => {
                        let storeAndCount = c.toString();
                        let secondaryCountByStore = this.secondaryCounts?.find(sc => sc.storeId == c.storeId)?.count;
                        if(secondaryCountByStore) storeAndCount+=` (${secondaryCountByStore})`;
                        return storeAndCount;
                    }).filter(c => c.length > 0).reduce((a,b) => `${a}<br/>${b}`, "");
                }
                else res = this.counts.filter(c => c.otherClassification).map(c => c.toString()).filter(c => c.length > 0).reduce((a,b) => `${a}<br/>${b}`, "");
            }
            if (this.extraTooltip) res += (res ? '<br/><br/>' : '') + this.extraTooltip;
            return res;
        }
    }

</script>

<style scoped>
.info-title {
    font-size: 1.4em;
}
.stats-title {
    font-size: .8em;
}
</style>
